/**
 * CriarAudienciaDialog — inclusão manual de uma audiência não cadastrada.
 *
 * POST /api/audiencias → cria o prazo (origem='manual') + card no Kanban e já o
 * marca como tipo_evento='audiencia' com data/hora. Para audiências que não
 * vieram do DJEN/triagem. Disponível a admin e colaborador.
 */
import { useState } from 'react';
import { useCriarAudiencia } from '../hooks';
import type { CriarAudienciaIn } from '../types';

interface Props {
  onClose: () => void;
}

function hojeIso(): string {
  const d = new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60_000).toISOString().slice(0, 10);
}

export function CriarAudienciaDialog({ onClose }: Props) {
  const [numero, setNumero] = useState('');
  const [data, setData] = useState(hojeIso());
  const [hora, setHora] = useState('09:00');
  const [tipo, setTipo] = useState('Audiência Designada');
  const [sigla, setSigla] = useState('');
  const [obs, setObs] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const mut = useCriarAudiencia();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!numero.trim() || !data) return;
    setErro(null);
    const body: CriarAudienciaIn = {
      numero_processo: numero.trim(),
      data_audiencia: data,
      hora_audiencia: hora || '09:00',
      tipo_prazo: tipo.trim() || 'Audiência Designada',
      sigla_tribunal: sigla.trim() || null,
      observacao: obs.trim() || null,
    };
    try {
      await mut.mutateAsync(body);
      onClose();
    } catch (err: any) {
      if (err.status === 409) setErro('Já existe um prazo com esse número/tipo.');
      else if (err.status === 403) setErro('Sem permissão para incluir audiências.');
      else setErro(err?.message ?? 'Erro ao incluir a audiência');
    }
  }

  const inputClasse =
    'w-full text-sm border border-gray-300 dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-md p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
            ⚖️ Incluir audiência
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-xl leading-none"
            aria-label="Fechar"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
              Número do processo *
            </label>
            <input
              value={numero}
              onChange={(e) => setNumero(e.target.value)}
              placeholder="0000000-00.0000.5.01.0000"
              className={inputClasse}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                Data da audiência *
              </label>
              <input
                type="date"
                value={data}
                onChange={(e) => setData(e.target.value)}
                className={inputClasse}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                Hora
              </label>
              <input
                type="time"
                value={hora}
                onChange={(e) => setHora(e.target.value)}
                className={inputClasse}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                Tipo
              </label>
              <input
                value={tipo}
                onChange={(e) => setTipo(e.target.value)}
                placeholder="Audiência Designada"
                className={inputClasse}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                Sigla do tribunal
              </label>
              <input
                value={sigla}
                onChange={(e) => setSigla(e.target.value)}
                placeholder="TRT1"
                className={inputClasse}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
              Observação (opcional)
            </label>
            <textarea
              value={obs}
              onChange={(e) => setObs(e.target.value)}
              rows={2}
              className={`${inputClasse} resize-none`}
            />
          </div>

          {erro && (
            <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded border border-red-200 dark:border-red-800">
              ⚠ {erro}
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={mut.isPending}
              className="flex-1 py-2 text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded text-gray-700 dark:text-gray-300 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={mut.isPending || !numero.trim() || !data}
              className="flex-1 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded font-medium"
            >
              {mut.isPending ? '⏳ Incluindo…' : '✅ Incluir audiência'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
